/**
 * Fixed asset register — `/finance/fixed-assets`.
 *
 * Capitalization is not a draft: it allocates the branch asset number, writes
 * the asset and generates its COMPLETE depreciation schedule — one row per
 * fiscal period for the entire useful life — in one transaction. So the form
 * says "capitalize", not "create", and warns that the fiscal calendar has to
 * reach the end of the useful life (`assets.fiscal_calendar_too_short`).
 *
 * Method, life, factor and switch default to the asset class and may be
 * overridden per asset; the overrides are optional fields that are simply
 * omitted when left alone, so the class default is what actually applies.
 */
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import { Button, Card, CardContent, CardHeader, CardTitle, Checkbox } from "@erp/ui";
import { Plus } from "lucide-react";

import { DataTable, badgeCell } from "@/components/data-table";
import { surfaceProblem } from "@/components/form";
import { MoneyDisplay } from "@/components/money/money-display";
import { useIdempotentMutation } from "@/features/shared/ops-api";
import { useCurrentBranch } from "@/lib/branch/branch-store";

import {
  DEPRECIATION_METHODS,
  FIXED_ASSET_STATUSES,
  capitalizeFixedAsset,
  type DepreciationMethod,
  type FixedAsset,
  type FixedAssetCreate,
} from "../api";
import { AMOUNT_SCALE, normalizeDecimal, todayIsoDate } from "../decimal";
import {
  DateField,
  DecimalField,
  NoticeBanner,
  PageHeader,
  ProblemBanner,
  SelectField,
  TextField,
} from "../components/fields";
import {
  financeKeys,
  useAssetClasses,
  useFixedAssetList,
  type FixedAssetFilters,
} from "../queries";

const ALL = "__all__";
const NONE = "__none__";
const INHERIT = "__inherit__";

const ASSET_STATUS_VARIANTS = {
  draft: "outline",
  active: "success",
  disposed: "secondary",
} as const;

export interface FixedAssetsPageProps {
  onOpenAsset: (fixedAssetId: string) => void;
  canCapitalize?: boolean;
  canReadClasses?: boolean;
  pageLimit?: number;
}

export function FixedAssetsPage({
  onOpenAsset,
  canCapitalize = true,
  canReadClasses = true,
  pageLimit,
}: FixedAssetsPageProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { branchId } = useCurrentBranch();

  const [statusFilter, setStatusFilter] = useState<string>(ALL);
  const [classFilter, setClassFilter] = useState<string>(ALL);
  const [capitalizing, setCapitalizing] = useState(false);

  const [classId, setClassId] = useState(NONE);
  const [description, setDescription] = useState("");
  const [cost, setCost] = useState("");
  const [salvage, setSalvage] = useState("");
  const [currency, setCurrency] = useState("SAR");
  const [capitalizationDate, setCapitalizationDate] = useState(todayIsoDate);
  const [method, setMethod] = useState<string>(INHERIT);
  const [usefulLife, setUsefulLife] = useState("");
  const [decliningFactor, setDecliningFactor] = useState("");
  const [switchToStraightLine, setSwitchToStraightLine] = useState<boolean | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const classesQuery = useAssetClasses(canReadClasses);
  const classes = useMemo(() => classesQuery.data ?? [], [classesQuery.data]);

  const filters = useMemo<FixedAssetFilters>(() => {
    const built: FixedAssetFilters = {};
    if (statusFilter !== ALL) built.status = statusFilter as FixedAsset["status"];
    if (classFilter !== ALL) built.class_id = classFilter;
    return built;
  }, [statusFilter, classFilter]);

  const listQuery = useFixedAssetList(filters, pageLimit);

  const className = (id: string): string => {
    const found = classes.find((candidate) => candidate.id === id);
    return found === undefined ? id : `${found.code} · ${found.name}`;
  };

  const capitalizeMutation = useIdempotentMutation<FixedAsset, FixedAssetCreate>({
    mutationFn: (body, key) => capitalizeFixedAsset(body, key),
    onSuccess: (asset) => {
      void queryClient.invalidateQueries({ queryKey: financeKeys.fixedAssets.all });
      setCapitalizing(false);
      onOpenAsset(asset.id);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const submit = () => {
    if (branchId === null) {
      setFormError(
        t(
          "finance.common.branchRequired",
          "Select an acting branch before creating an operational document.",
        ),
      );
      return;
    }
    if (classId === NONE) {
      setFormError(t("finance.fixedAssets.classRequired", "Choose the asset class."));
      return;
    }
    if (description.trim() === "") {
      setFormError(t("finance.fixedAssets.descriptionRequired", "Describe the asset."));
      return;
    }
    const acquisitionCost = normalizeDecimal(cost, { scale: AMOUNT_SCALE, allowZero: false });
    if (acquisitionCost === null) {
      setFormError(
        t(
          "finance.fixedAssets.costInvalid",
          "Enter the acquisition cost with at most four decimals.",
        ),
      );
      return;
    }
    const salvageValue =
      salvage.trim() === "" ? null : normalizeDecimal(salvage, { scale: AMOUNT_SCALE });
    if (salvage.trim() !== "" && salvageValue === null) {
      setFormError(
        t(
          "finance.fixedAssets.salvageInvalid",
          "Enter the salvage value with at most four decimals.",
        ),
      );
      return;
    }
    const life = usefulLife.trim() === "" ? null : Number.parseInt(usefulLife, 10);
    if (life !== null && (Number.isNaN(life) || life < 1 || life > 1200)) {
      setFormError(
        t("finance.fixedAssets.lifeInvalid", "Useful life is between 1 and 1200 months."),
      );
      return;
    }
    const factor =
      decliningFactor.trim() === "" ? null : normalizeDecimal(decliningFactor, { scale: 6 });
    if (decliningFactor.trim() !== "" && factor === null) {
      setFormError(
        t("finance.fixedAssets.factorInvalid", "The declining factor is above 1 and at most 5."),
      );
      return;
    }
    setFormError(null);
    capitalizeMutation.mutate({
      branch_id: branchId,
      class_id: classId,
      description: description.trim(),
      acquisition_cost: acquisitionCost,
      currency: currency.trim().toUpperCase(),
      capitalization_date: capitalizationDate,
      ...(salvageValue === null ? {} : { salvage_value: salvageValue }),
      ...(method === INHERIT ? {} : { method: method as DepreciationMethod }),
      ...(life === null ? {} : { useful_life_months: life }),
      ...(factor === null ? {} : { declining_factor: factor }),
      ...(switchToStraightLine === null ? {} : { switch_to_straight_line: switchToStraightLine }),
    });
  };

  const columns = useMemo<ColumnDef<FixedAsset>[]>(
    () => [
      {
        accessorKey: "asset_no",
        id: "asset_no",
        header: t("finance.fixedAssets.columns.assetNo", "Asset"),
        enableSorting: false,
        cell: (ctx) => (
          <span dir="ltr" className="font-mono font-medium">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: "description",
        id: "description",
        header: t("finance.fixedAssets.columns.description", "Description"),
        enableSorting: false,
      },
      {
        accessorKey: "class_id",
        id: "class_id",
        header: t("finance.fixedAssets.columns.class", "Class"),
        enableSorting: false,
        cell: (ctx) => className(ctx.getValue<string>()),
      },
      {
        accessorKey: "capitalization_date",
        id: "capitalization_date",
        header: t("finance.fixedAssets.columns.capitalized", "Capitalized"),
        enableSorting: false,
        cell: (ctx) => <span dir="ltr">{ctx.getValue<string>()}</span>,
      },
      {
        accessorKey: "status",
        id: "status",
        header: t("finance.fixedAssets.columns.status", "Status"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string>();
          return badgeCell(
            value,
            ASSET_STATUS_VARIANTS,
            t(`finance.fixedAssets.statusOption.${value}`, value),
          );
        },
      },
      {
        id: "acquisition_cost",
        header: t("finance.fixedAssets.columns.cost", "Cost"),
        enableSorting: false,
        meta: { align: "end" },
        cell: (ctx) => <MoneyDisplay value={ctx.row.original.acquisition_cost} />,
      },
      {
        id: "net_book_value",
        header: t("finance.fixedAssets.columns.nbv", "Net book value"),
        enableSorting: false,
        meta: { align: "end" },
        cell: (ctx) => <MoneyDisplay value={ctx.row.original.net_book_value} />,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- class lookup closes over stable query data
    [t, classes],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <PageHeader
        title={t("finance.fixedAssets.title", "Fixed assets")}
        description={t(
          "finance.fixedAssets.subtitle",
          "The register, at historical cost, with depreciation that has actually posted.",
        )}
        actions={
          canCapitalize ? (
            <Button type="button" onClick={() => setCapitalizing((current) => !current)}>
              <Plus className="size-4" aria-hidden="true" />
              {t("finance.fixedAssets.capitalize", "Capitalize an asset")}
            </Button>
          ) : null
        }
      />

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <SelectField
            id="asset-status-filter"
            label={t("finance.fixedAssets.filterStatus", "Status")}
            value={statusFilter}
            onValueChange={setStatusFilter}
            className="w-44"
            options={[
              { value: ALL, label: t("finance.common.all", "All") },
              ...FIXED_ASSET_STATUSES.map((value) => ({
                value,
                label: t(`finance.fixedAssets.statusOption.${value}`, value),
              })),
            ]}
          />
          <SelectField
            id="asset-class-filter"
            label={t("finance.fixedAssets.filterClass", "Class")}
            value={classFilter}
            onValueChange={setClassFilter}
            className="w-72"
            disabled={!canReadClasses}
            options={[
              { value: ALL, label: t("finance.common.all", "All") },
              ...classes.map((assetClass) => ({
                value: assetClass.id,
                label: `${assetClass.code} · ${assetClass.name}`,
              })),
            ]}
          />
        </CardContent>
      </Card>

      {capitalizing && canCapitalize ? (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("finance.fixedAssets.capitalizeSection", "Capitalize an asset")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {branchId === null ? (
              <div className="sm:col-span-2 lg:col-span-3">
                <NoticeBanner>
                  {t(
                    "finance.common.branchRequired",
                    "Select an acting branch before creating an operational document.",
                  )}
                </NoticeBanner>
              </div>
            ) : null}
            <SelectField
              id="asset-class"
              label={t("finance.fixedAssets.class", "Asset class")}
              value={classId}
              onValueChange={setClassId}
              options={[
                { value: NONE, label: t("finance.common.choose", "Choose…") },
                ...classes.map((assetClass) => ({
                  value: assetClass.id,
                  label: `${assetClass.code} · ${assetClass.name}`,
                })),
              ]}
              disabled={!canReadClasses}
            />
            <TextField
              id="asset-description"
              label={t("finance.fixedAssets.description", "Description")}
              value={description}
              onChange={setDescription}
              className="lg:col-span-2"
              maxLength={512}
            />
            <DecimalField
              id="asset-cost"
              label={t("finance.fixedAssets.cost", "Acquisition cost")}
              value={cost}
              onChange={setCost}
              decimal={{ scale: AMOUNT_SCALE, allowZero: false }}
              invalidMessage={t(
                "finance.fixedAssets.costInvalid",
                "Enter the acquisition cost with at most four decimals.",
              )}
            />
            <DecimalField
              id="asset-salvage"
              label={t("finance.fixedAssets.salvage", "Salvage value")}
              value={salvage}
              onChange={setSalvage}
              decimal={{ scale: AMOUNT_SCALE }}
              hint={t(
                "finance.fixedAssets.salvageHint",
                "Defaults to zero. Never depreciated below it.",
              )}
              invalidMessage={t(
                "finance.fixedAssets.salvageInvalid",
                "Enter the salvage value with at most four decimals.",
              )}
            />
            <TextField
              id="asset-currency"
              label={t("finance.common.currency", "Currency")}
              value={currency}
              onChange={(value) => setCurrency(value.toUpperCase())}
              hint={t(
                "finance.fixedAssets.currencyHint",
                "Must be the company's functional currency — assets are held at historical cost.",
              )}
              ltr
              maxLength={3}
            />
            <DateField
              id="asset-capitalization-date"
              label={t("finance.fixedAssets.capitalizationDate", "Capitalization date")}
              value={capitalizationDate}
              onChange={setCapitalizationDate}
            />
            <SelectField
              id="asset-method"
              label={t("finance.fixedAssets.method", "Depreciation method")}
              value={method}
              onValueChange={setMethod}
              options={[
                {
                  value: INHERIT,
                  label: t("finance.fixedAssets.inherit", "Inherit from the class"),
                },
                ...DEPRECIATION_METHODS.map((value) => ({
                  value,
                  label: t(`finance.fixedAssets.methodOption.${value}`, value),
                })),
              ]}
            />
            <TextField
              id="asset-useful-life"
              label={t("finance.fixedAssets.usefulLife", "Useful life (months)")}
              value={usefulLife}
              onChange={setUsefulLife}
              hint={t(
                "finance.fixedAssets.inheritHint",
                "Leave empty to inherit the class default.",
              )}
              ltr
            />
            <DecimalField
              id="asset-declining-factor"
              label={t("finance.fixedAssets.decliningFactor", "Declining factor")}
              value={decliningFactor}
              onChange={setDecliningFactor}
              decimal={{ scale: 6 }}
              hint={t(
                "finance.fixedAssets.inheritHint",
                "Leave empty to inherit the class default.",
              )}
              invalidMessage={t(
                "finance.fixedAssets.factorInvalid",
                "The declining factor is above 1 and at most 5.",
              )}
            />
            <label className="flex items-center gap-2 text-sm sm:col-span-2 lg:col-span-3">
              <Checkbox
                checked={switchToStraightLine === true}
                onCheckedChange={(checked) => setSwitchToStraightLine(checked === true)}
              />
              {t(
                "finance.fixedAssets.switchToStraightLine",
                "Switch to straight line once it charges more",
              )}
            </label>

            <div className="sm:col-span-2 lg:col-span-3">
              <NoticeBanner>
                {t(
                  "finance.fixedAssets.scheduleWarning",
                  "The whole schedule is generated now. If the fiscal calendar does not reach the end of the useful life the request is refused rather than truncated.",
                )}
              </NoticeBanner>
            </div>

            {formError === null ? null : (
              <div className="sm:col-span-2 lg:col-span-3">
                <ProblemBanner>{formError}</ProblemBanner>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 sm:col-span-2 lg:col-span-3">
              <Button type="button" loading={capitalizeMutation.isPending} onClick={submit}>
                {t("finance.fixedAssets.capitalizeAction", "Capitalize and generate the schedule")}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setCapitalizing(false)}>
                {t("actions.cancel")}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <DataTable
        columns={columns}
        query={listQuery}
        getRowId={(row) => row.id}
        onRowClick={(row) => onOpenAsset(row.id)}
        storageKey="finance.fixedAssets.columns"
        emptyMessage={t("finance.fixedAssets.empty", "No assets match these filters.")}
      />
    </div>
  );
}
